import { Component, inject, input, resource, signal } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { BooleanPipe } from '@core/pipes/boolean.pipe';
import { PersonHelbredApi } from '@features/helbred/person-helbred/person-helbred.api';
import { Message } from 'primeng/message';
import { SimpleApi } from '@core/api/simple.api';
import { HelbredGodkendelse } from '@features/helbred/person-helbred/helbred-godkendelse.model';
import dayjs from 'dayjs';
import { CheckmarkComponent } from '@shared/checkmark/checkmark.component';
import { DatePicker } from 'primeng/datepicker';
import { FormsModule } from '@angular/forms';
import { wait } from '@core/api/api-utils';
import { KunderService } from '@features/kunder/kunder.service';
import { AuthService } from '@core/auth/auth.service';
import { RettighedsType } from '@core/auth/rettighed.model';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { Kunde } from '@features/kunder/kunde.model';

@Component({
  selector: 'app-person-helbred',
  imports: [
    FormsModule,
    DatePipe,
    BooleanPipe,
    Message,
    DecimalPipe,
    CheckmarkComponent,
    DatePicker,
    ButtonModule,
    DialogModule,
    InputNumber,
  ],
  templateUrl: './person-helbred.component.html',
  styleUrl: './person-helbred.component.css',
})
export class PersonHelbredComponent {
  authService = inject(AuthService);
  kundeService = inject(KunderService);
  personHelbredApi = inject(PersonHelbredApi);
  simpleApi = inject(SimpleApi);
  kunde = input<Kunde>();
  showManueltHelbredDialog = signal(false);
  manuelScoreSygdom = signal<number | undefined>(undefined);
  manuelScoreDoed = signal<number | undefined>(undefined);
  manuelScoreKritiskSygdom = signal<number | undefined>(undefined);
  manuelScoreJob = signal<number | undefined>(undefined);
  saving = signal(false);

  data = resource({
    params: () => {
      const dato = dayjs(this.kundeService.helbredsDato()).format(
        'YYYY-MM-DD HH:mm:ss'
      );
      return {
        personId: this.kunde()?.personOplysningerId,
        dato: dato,
      };
    },
    loader: async ({ params, abortSignal }) => {
      await wait(300, abortSignal);
      const response = await this.personHelbredApi.getList(
        {
          filter: JSON.stringify(params),
          sortBy: 'id',
          descending: true,
        },
        abortSignal
      );

      if (response.data.length > 0) {
        return response.data[0];
      }
      return undefined;
    },
  });

  helbredsgodkendelse = resource({
    params: () => {
      return {
        personId: this.kunde()?.personOplysningerId,
        dato: dayjs(this.kundeService.helbredsDato()).format('YYYY-MM-DD'),
      };
    },
    loader: async ({ params, abortSignal }) => {
      await wait(300, abortSignal);
      return await this.simpleApi.getList<HelbredGodkendelse>(
        '/api/admin/personhelbred/godkendelse',
        {
          ...params,
        },
        abortSignal
      );
    },
  });

  getHelbredsgodkendelse(rammeType: string) {
    const list = this.helbredsgodkendelse.value() || [];
    const found = list.filter(x => x.rammeType === rammeType);
    if (found.length > 0) {
      return found[0].godkendt;
    }
    return false;
  }

  getHelbredsgodkendelseFarve() {
    const list = this.helbredsgodkendelse.value() || [];
    const uniqueValues = [...new Set(list.map(x => x.godkendt))];
    if (uniqueValues.length > 1) {
      return { text: 'Gult', className: 'helbred-yellow' };
    }
    if (uniqueValues[0]) {
      return { text: 'Grønt', className: 'helbred-green' };
    }
    return { text: 'Rødt', className: 'helbred-red' };
  }

  allowIndtastHelbred() {
    return this.authService.hasRettighed(RettighedsType.AdminManuelHelbred);
  }

  onClickEnterManueltHelbred() {
    this.manuelScoreSygdom.set(this.data.value()?.sygdomScore);
    this.manuelScoreDoed.set(this.data.value()?.doedScore);
    this.manuelScoreKritiskSygdom.set(this.data.value()?.kritiskSygdomScore);
    this.manuelScoreJob.set(this.data.value()?.jobScore);
    this.showManueltHelbredDialog.set(true);
  }

  onClickCancelManueltHelbred() {
    this.showManueltHelbredDialog.set(false);
  }

  messages = signal<string[]>([]);

  async onClickSaveManueltHelbred() {
    this.messages.set([]);

    const sygdom = this.manuelScoreSygdom();
    if (!sygdom || sygdom < 1 || sygdom > 999) {
      this.messages.update(m => [
        ...m,
        'Værdien for sygdom skal være mellem 1 og 999',
      ]);
    }

    const doed = this.manuelScoreDoed();
    if (!doed || doed < 1 || doed > 999) {
      this.messages.update(m => [
        ...m,
        'Værdien for død skal være mellem 1 og 999',
      ]);
    }

    const kritiskSygdom = this.manuelScoreKritiskSygdom();
    if (kritiskSygdom && (kritiskSygdom < 1 || kritiskSygdom > 999)) {
      this.messages.update(m => [
        ...m,
        'Værdien for kritisk sygdom skal være mellem 1 og 999, eller ikke udfyldt',
      ]);
    }

    const job = this.manuelScoreJob();
    const gyldigJobScore = [1, 1.25, 1.75, 2.25, 2.5, 666, 999];
    if (!job || !gyldigJobScore.includes(job)) {
      this.messages.update(m => [
        ...m,
        'Jobscore  skal være en af følgende (' +
          gyldigJobScore.join(', ') +
          ')',
      ]);
    }

    if (this.messages().length > 0) {
      return;
    }

    this.saving.set(true);
    await this.personHelbredApi.create({
      personId: this.kunde()?.personOplysningerId,
      kundeId: this.kunde()?.id,
      sygdomScore: this.manuelScoreSygdom(),
      doedScore: this.manuelScoreDoed(),
      kritiskSygdomScore: this.manuelScoreKritiskSygdom(),
      jobScore: this.manuelScoreJob(),
    });
    this.kundeService.reSelectKunde(); //Triggers reload
    this.showManueltHelbredDialog.set(false);
    this.saving.set(false);
  }
}
